import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import clsx from 'clsx';
import {
  Grid,
  Typography,
  Button,
  CircularProgress,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  HighlightOff as HighlightOffIcon,
} from '@material-ui/icons';
// Components
import { SectionWrapper, ToggleTickSwitch, CheckBox } from 'components';
import PaymentMethodStatusBadge from './PaymentMethodStatusBadge';

// Services
import { USER_SERVICES } from 'Services';

// Context
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';

// Constants
import { PAYMENT_TYPES } from 'Views/Common/enum';

// Utils
import Utils from 'Shared/Utils';
import { BILLING_FREQUENCY } from 'Shared/GroupUtils';

// Styles
import styles from './PaymentCapabilities.module.scss';
import useStyles from './styles';



const getPaymentTypeLabel = (paymentType) =>
  PAYMENT_TYPES[paymentType?.display] ||
  paymentType?.display ||
  paymentType?.name;

const PaymentCapabilities = (props) => {
  const {
    isLocationPaymentMode = false,
    levelData = {},
    settings: {
      provider_payment_types,
      setting_level,
      preferred_billing_frequency = BILLING_FREQUENCY.ANNUAL,
    } = {},
    onUpdate,
  } = props;

  const baselineProviderPaymentTypes = provider_payment_types ?? [];
  const hasLocationPaymentTypeOverrides =
    isLocationPaymentMode && baselineProviderPaymentTypes.length > 0;

  const { partner_id, group_id, region_id, location_id } = levelData;

  const {
    state: {
      paymentTypes: { list: paymentTypeList, loading: paymentTypeLoading } = {},
    },
    dispatch: globalDispatch,
  } = useContext(Context);

  const classes = useStyles();

  // ── Payment Mode ─────────────────────────────────────────────
  const [providerPaymentTypes, setProviderPaymentTypes] = useState(baselineProviderPaymentTypes);

  // ── Billing Frequency ────────────────────────────────────────
  const [billingFrequency, setBillingFrequency] = useState(
    preferred_billing_frequency || BILLING_FREQUENCY.ANNUAL
  );
  const initialBillingFrequency = preferred_billing_frequency || BILLING_FREQUENCY.ANNUAL;

  // ── Save state ───────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);

  // ── Location mode state ──────────────────────────────────────
  const [isOverrideActive, setIsOverrideActive] = useState(hasLocationPaymentTypeOverrides);
  const [groupSettingsData, setGroupSettingsData] = useState({
    level_data: {},
    settings: {},
  });

  // ── Dirty detection ──────────────────────────────────────────
  const checkIfEqual = (updatedArray, initArray) => {
    const sortedUpdated = updatedArray.slice().sort();
    const sortedInit = initArray.slice().sort();
    return !_.isEqual(sortedUpdated, sortedInit);
  };

  const paymentTypeUpdated = checkIfEqual(providerPaymentTypes, baselineProviderPaymentTypes);
  const isBillingFrequencyUpdated = billingFrequency !== initialBillingFrequency;

  const hasChanges = paymentTypeUpdated || isBillingFrequencyUpdated;

  const overrideToggleUpdated = isOverrideActive !== hasLocationPaymentTypeOverrides;
  const overrideTypesUpdated =
    isOverrideActive &&
    checkIfEqual(providerPaymentTypes, baselineProviderPaymentTypes);
  const locationPaymentUpdated = overrideToggleUpdated || overrideTypesUpdated;

  const showSaveCancel = isLocationPaymentMode ? locationPaymentUpdated : hasChanges;

  // ── Fetch group settings (location mode only) ─────────────────
  const fetchGroupSettings = async () => {
    if (!group_id) return;

    try {
      const params = Utils.getQueryString({ partner_id });
      const response = await USER_SERVICES.getSettings(
        'group',
        group_id,
        params,
      );

      if (response?.type === 'success') {
        const { level_data = {}, settings = {} } = response?.data || {};
        setGroupSettingsData({ level_data, settings });
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (isLocationPaymentMode) {
      fetchGroupSettings();
    }
  }, [isLocationPaymentMode, group_id, partner_id]);

  // Sync settings props changes
  useEffect(() => {
    setProviderPaymentTypes(provider_payment_types ?? []);
  }, [provider_payment_types]);

  useEffect(() => {
    setIsOverrideActive(hasLocationPaymentTypeOverrides);
  }, [hasLocationPaymentTypeOverrides]);

  useEffect(() => {
    setBillingFrequency(preferred_billing_frequency || BILLING_FREQUENCY.ANNUAL);
  }, [preferred_billing_frequency]);

  // ── Save ─────────────────────────────────────────────────────
  const saveSettingsData = async () => {
    setIsLoading(true);
    try {
      const levelPayload = {
        [setting_level]: { location_id, region_id, group_id, partner_id },
      };

      if (isLocationPaymentMode) {
        // Location mode: save only payment types (or null to clear override)
        const call = await USER_SERVICES.updateLevels(
          {
            ...levelPayload,
            setting: {
              provider_payment_types: isOverrideActive ? providerPaymentTypes : null,
            },
          },
          setting_level
        );
        if (call?.type !== 'success') {
          throw call?.message || call?.error;
        }
      } else {
        // Call 1 — payment mode + billing frequency
        const call1 = await USER_SERVICES.updateLevels(
          {
            ...levelPayload,
            setting: {
              provider_payment_types: providerPaymentTypes,
              preferred_billing_frequency: billingFrequency,
            },
          },
          setting_level
        );
        if (call1?.type !== 'success') {
          throw call1?.message || call1?.error || 'Error updating payment type and billing frequency';
        }
      }

      dispatchNotification('success', 'Capability updated successfully');
      onUpdate?.('Settings');
    } catch (err) {
      dispatchNotification('error', err || 'Error updating capability details');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelChanges = () => {
    setProviderPaymentTypes(baselineProviderPaymentTypes);
    setIsOverrideActive(hasLocationPaymentTypeOverrides);

    if (!isLocationPaymentMode) {
      setBillingFrequency(initialBillingFrequency);
    }
  };

  const dispatchNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });

  const handleChecked = (chipVal, type = 'add') => {
    const settingPaymentTypes = _.cloneDeep(providerPaymentTypes);
    const newValue =
      type === 'add'
        ? [...settingPaymentTypes, chipVal?.name]
        : settingPaymentTypes.filter((p) => p !== chipVal?.name);
    setProviderPaymentTypes(newValue);
  };

  const handleOverrideToggle = (checked) => {
    setIsOverrideActive(checked);

    if (checked && !hasLocationPaymentTypeOverrides) {
      setProviderPaymentTypes(
        groupSettingsData.settings?.provider_payment_types ?? [],
      );
    }
  };

  const handleBillingFrequencyChange = (event) => {
    setBillingFrequency(
      event?.target?.checked ? BILLING_FREQUENCY.ANNUAL : BILLING_FREQUENCY.ANNUAL_BILLED_MONTHLY
    );
  };

  const renderPaymentTypeCheckboxes = (paymentTypes) =>
    paymentTypes?.map((indPayment) => {
      const isSelected = providerPaymentTypes.find(
        (pType) => pType === indPayment?.name,
      );

      return (
        <React.Fragment key={indPayment?.name}>
          <CheckBox
            checked={isSelected === indPayment?.name}
            value={indPayment?.name}
            disabled={!indPayment?.optional}
            onChange={() =>
              handleChecked(indPayment, isSelected ? 'delete' : 'add')
            }
          />
          <Typography variant="body1" className="mr-30">
            {getPaymentTypeLabel(indPayment)}
          </Typography>
        </React.Fragment>
      );
    });

  const renderLocationPaymentSection = () => {
    const groupPaymentTypes =
      groupSettingsData.settings?.provider_payment_types ?? [];
    const groupName = groupSettingsData.level_data?.group_name || '';

    return (
      <>
        <Grid item xs={12} className={styles.groupLevelSection}>
          <div className={styles.groupLevelHeaderRow}>
            <Typography component="span" className={styles.sectionTitle}>
              Group Level - {groupName}
            </Typography>
            <div className={styles.overrideToggleRow}>
              <ToggleTickSwitch
                className="d-flex align-center"
                checked={isOverrideActive}
                onChange={(e) => handleOverrideToggle(e.target.checked)}
                name="override_payment_types"
                inputProps={{
                  'aria-label': 'Override group settings',
                  'data-testid': 'toggle-override-payment-types',
                }}
              />
              <Typography component="span" className={styles.sectionTitle}>
                Override Group Settings
              </Typography>
            </div>
          </div>
          {!paymentTypeLoading && (
            <div className={styles.groupLevelRow}>
              {paymentTypeList.map((indPayment) => (
                <PaymentMethodStatusBadge
                  key={indPayment?.name}
                  label={getPaymentTypeLabel(indPayment)}
                  enabled={groupPaymentTypes.includes(indPayment?.name)}
                />
              ))}
            </div>
          )}
        </Grid>

        {isOverrideActive && (
          <Grid item xs={12} className={styles.overrideSection}>
            <Typography component="span" className={styles.sectionTitle}>
              Override for this location
            </Typography>
            {!paymentTypeLoading && (
              <div className={styles.overrideCheckboxesRow}>
                {renderPaymentTypeCheckboxes(paymentTypeList)}
              </div>
            )}
          </Grid>
        )}
      </>
    );
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <SectionWrapper
      title="Payment Capabilities"
      rightSection={
        <>
          {showSaveCancel && (
            <>
              <Button
                size="small"
                onClick={saveSettingsData}
                disabled={isLoading}
                data-testid="save-payment-changes-button"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="primary" />
                  ) : (
                    <SaveIcon color="secondary" />
                  )
                }
              >
                <Typography color="secondary">Save Changes</Typography>
              </Button>
              <Button
                size="small"
                className="mg_left_8"
                onClick={cancelChanges}
                disabled={isLoading}
                data-testid="cancel-payment-changes-button"
                startIcon={
                  <HighlightOffIcon disabled={isLoading} className="theme_color_orange" />
                }
              >
                <Typography className="theme_color_orange">Cancel</Typography>
              </Button>
            </>
          )}
        </>
      }
    >
      <Grid container spacing={3}>
        {isLocationPaymentMode && renderLocationPaymentSection()}

        {!isLocationPaymentMode && (
          <>
            {/* 1. Payment Mode */}
            <Grid item xs={12} className="mg_bottom_10">
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={2} className={styles.paymentModeTitle}>
                  <Typography variant="body1">Payment Mode</Typography>
                </Grid>
                {!paymentTypeLoading && (
                  <Grid item xs={10} className="d-flex align-center">
                    {paymentTypeList?.map((indPayment) => {
                      const isSelected = providerPaymentTypes.find((p) => p === indPayment?.name);
                      return (
                        <React.Fragment key={indPayment?.name}>
                          <CheckBox
                            key={indPayment}
                            checked={isSelected === indPayment?.name}
                            value={indPayment?.name}
                            disabled={!indPayment?.optional}
                            onChange={() =>
                              handleChecked(indPayment, isSelected ? 'delete' : 'add')
                            }
                          />
                          <Typography variant="body1" className="mr-30">
                            {getPaymentTypeLabel(indPayment)}
                          </Typography>
                        </React.Fragment>
                      );
                    })}
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* 2. Billing Frequency */}
            <Grid item xs={12}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} md={3} lg={2} className={styles.toggleTitle}>
                  <Typography variant="body1">Default Billing Frequency</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3} className="d-flex align-center">
                  <Typography variant="body1">Monthly</Typography>
                  <ToggleTickSwitch
                    className={clsx('mg_left_8 d-flex align-center', classes.billingFrequencySwitch)}
                    checked={billingFrequency === BILLING_FREQUENCY.ANNUAL}
                    onChange={handleBillingFrequencyChange}
                    name="billing_frequency"
                    inputProps={{
                      'aria-label': 'Billing frequency toggle',
                      'data-testid': 'toggle-billing-frequency',
                    }}
                  />
                  <Typography variant="body1">Annually</Typography>
                </Grid>
              </Grid>
            </Grid>

            </>
          )
        }
      </Grid>
    </SectionWrapper>
  );
};

PaymentCapabilities.propTypes = {
  isLocationPaymentMode: PropTypes.bool,
  levelData: PropTypes.object,
  settings: PropTypes.object,
  onUpdate: PropTypes.func,
};

export default PaymentCapabilities;
